import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Box, Button, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

const MIN_W = 300;
const MIN_H = 180;

// Resize handles: thin strips on each edge + small squares on each corner.
const RESIZE_HANDLES = [
  { key: "t", dirs: { top: true }, sx: { top: 0, left: 10, right: 10, height: 6, cursor: "ns-resize" } },
  { key: "b", dirs: { bottom: true }, sx: { bottom: 0, left: 10, right: 10, height: 6, cursor: "ns-resize" } },
  { key: "l", dirs: { left: true }, sx: { left: 0, top: 10, bottom: 10, width: 6, cursor: "ew-resize" } },
  { key: "r", dirs: { right: true }, sx: { right: 0, top: 10, bottom: 10, width: 6, cursor: "ew-resize" } },
  { key: "tl", dirs: { top: true, left: true }, sx: { top: 0, left: 0, width: 14, height: 14, cursor: "nwse-resize", zIndex: 3 } },
  { key: "tr", dirs: { top: true, right: true }, sx: { top: 0, right: 0, width: 14, height: 14, cursor: "nesw-resize", zIndex: 3 } },
  { key: "bl", dirs: { bottom: true, left: true }, sx: { bottom: 0, left: 0, width: 14, height: 14, cursor: "nesw-resize", zIndex: 3 } },
  { key: "br", dirs: { bottom: true, right: true }, sx: { bottom: 0, right: 0, width: 14, height: 14, cursor: "nwse-resize", zIndex: 3 } },
];

/**
 * A non-modal, draggable AND resizable help panel. Unlike a Dialog it does not block
 * the page — the user can drag it (by its title bar), resize it from any edge/corner,
 * and keep filling the form while it stays open. It only closes via the X / Close
 * button. Position and size are remembered between opens.
 */
const FloatingHelpPanel = ({
  open,
  title,
  onClose,
  width = 600,
  height,
  // Optional initial position (w, h) => { x, y }. Defaults to the top-right;
  // plot panels use this to avoid opening on top of the main results panel.
  initialPos,
  // Keep the content mounted (hidden) when closed, so panel state — and any
  // side effects it drives, like the microclimate slice — survives a close/open.
  keepMounted = false,
  // Optional node rendered in the footer, LEFT of Close (e.g. a "Download CSV"
  // action). Kept here rather than in the body so panel actions sit where users
  // already look for them.
  footerActions = null,
  children,
}) => {
  const [pos, setPos] = useState(null); // { x, y } in viewport px
  const [size, setSize] = useState(null); // { width, height } in px
  const panelRef = useRef(null);
  const contentRef = useRef(null);

  // Initialize position/size SYNCHRONOUSLY the first time it opens (adjusting
  // state during render), so the panel — and any chart refs inside it — exist
  // in the DOM before consumer effects run, not a frame later. Placed near the
  // top-right by default so the content on the left stays visible.
  if (open && (pos === null || size === null)) {
    const w = Math.min(width, window.innerWidth - 24);
    const h = Math.min(height || 520, window.innerHeight - 120);
    const custom = typeof initialPos === "function" ? initialPos(w, h) : initialPos;
    setSize({ width: w, height: h });
    setPos(custom || { x: Math.max(16, window.innerWidth - w - 48), y: 96 });
  }

  // Keep the wheel over the panel scrolling the panel's content, never the page.
  // Over the content the browser scrolls it natively (overscroll-behavior:contain
  // stops it chaining to the page at the edges); over the title bar / footer / borders
  // we redirect the scroll into the content and block the page from moving.
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return undefined;
    const handleWheel = (e) => {
      const content = contentRef.current;
      if (!content) return;
      if (content.contains(e.target)) return;
      e.preventDefault();
      const factor =
        e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? content.clientHeight : 1;
      content.scrollTop += e.deltaY * factor;
    };
    panel.addEventListener("wheel", handleWheel, { passive: false });
    return () => panel.removeEventListener("wheel", handleWheel);
  }, [open, pos, size]);

  const startDrag = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const start = { mx: e.clientX, my: e.clientY, x: pos.x, y: pos.y };
    const onMove = (ev) => {
      const x = Math.min(
        Math.max(80 - size.width, start.x + ev.clientX - start.mx),
        window.innerWidth - 80,
      );
      const y = Math.min(
        Math.max(0, start.y + ev.clientY - start.my),
        window.innerHeight - 40,
      );
      setPos({ x, y });
    };
    const onUp = () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
    };
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
  };

  const startResize = (dirs) => (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const start = { mx: e.clientX, my: e.clientY, x: pos.x, y: pos.y, w: size.width, h: size.height };
    const maxW = window.innerWidth - 16;
    const maxH = window.innerHeight - 16;
    const onMove = (ev) => {
      const dx = ev.clientX - start.mx;
      const dy = ev.clientY - start.my;
      let x = start.x;
      let y = start.y;
      let w = start.w;
      let h = start.h;
      if (dirs.right) w = start.w + dx;
      if (dirs.bottom) h = start.h + dy;
      if (dirs.left) {
        w = start.w - dx;
        x = start.x + dx;
      }
      if (dirs.top) {
        h = start.h - dy;
        y = start.y + dy;
      }
      // Clamp to [min, max], keeping the opposite edge fixed when resizing left/top.
      const clampedW = Math.min(Math.max(w, MIN_W), maxW);
      if (dirs.left) x += w - clampedW;
      w = clampedW;
      const clampedH = Math.min(Math.max(h, MIN_H), maxH);
      if (dirs.top) y += h - clampedH;
      h = clampedH;
      setPos({ x, y });
      setSize({ width: w, height: h });
    };
    const onUp = () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
    };
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
  };

  // keepMounted: once opened, stay mounted but hidden when closed so panel state
  // (and the effects it drives) survives. Otherwise unmount on close.
  if (!keepMounted && !open) return null;
  if (!pos || !size) return null; // not yet positioned (never opened)
  const hidden = !open;

  // Render through a portal to <body> so the fixed panel escapes any ancestor
  // stacking context (e.g. the advanced-setup tab) and stays above all page content.
  return createPortal(
    <Box
      ref={panelRef}
      sx={{
        position: "fixed",
        top: pos.y,
        left: pos.x,
        width: size.width,
        height: size.height,
        zIndex: 2000,
        bgcolor: "#fff",
        borderRadius: 2,
        boxShadow: 8,
        border: "1px solid rgba(15, 23, 42, 0.18)",
        display: hidden ? "none" : "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Resize handles (each edge + corner) */}
      {RESIZE_HANDLES.map((handle) => (
        <Box
          key={handle.key}
          onPointerDown={startResize(handle.dirs)}
          sx={{ position: "absolute", zIndex: 2, ...handle.sx }}
        />
      ))}

      <Box
        onPointerDown={startDrag}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 1,
          bgcolor: "#1e3a5f",
          color: "#fff",
          cursor: "move",
          userSelect: "none",
          touchAction: "none",
        }}
      >
        <DragIndicatorIcon fontSize="small" />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, flexGrow: 1 }}>
          {title}
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          onPointerDown={(e) => e.stopPropagation()}
          sx={{ color: "#fff" }}
          aria-label="Close field guide"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box
        ref={contentRef}
        sx={{
          p: 2,
          overflow: "auto",
          flex: 1,
          minHeight: 0,
          // Stop scroll from chaining to the page when reaching the top/bottom.
          overscrollBehavior: "contain",
        }}
      >
        {children}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          justifyContent: footerActions ? "space-between" : "flex-end",
          px: 1.5,
          py: 1,
          borderTop: "1px solid rgba(15, 23, 42, 0.12)",
          bgcolor: "#f8fafc",
        }}
      >
        {footerActions}
        <Button size="small" variant="outlined" onClick={onClose}>
          Close
        </Button>
      </Box>
    </Box>,
    document.body,
  );
};

export default FloatingHelpPanel;
